import Cta from "@components/Cta/Cta";
import { NextContentfulImage } from "@components/Image";
import Link from "@components/Link/Link";
import type { TypeImageWrapperSkeleton } from "@customTypes/contentful";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { commaConjunction } from "@utilities/commaConjunction";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import { A11y, Keyboard, Pagination } from "swiper/modules";
import { Swiper as SwiperCarousel, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/a11y";
import "swiper/scss/pagination";
import videoIcon from "../../public/assets2019/img/vidicon.png";

import styles from "./C19_PromotionalArticles.module.scss";

export type ArticlePromotionType = {
  slug: string;
  title: string;
  description: string;
  image: ImageWrapperType | TypeImageWrapperSkeleton;
  subtitle?: string;
  authors?: string[];
  isVideo?: boolean;
};

export interface PromotionalArticlesProps {
  articles: ArticlePromotionType[];
  ctaText?: string;
  isBlue?: boolean;
}

export const PromotionalArticles = (props: PromotionalArticlesProps) => {
  const { articles, isBlue, ctaText } = props;
  const device = useDevice();
  const isDesktop = ["desktop", "desktop-large"].includes(device);

  const buildArticles = (articles: ArticlePromotionType[]) => {
    return articles?.map((article, index) => {
      return isDesktop ? (
        <ArticlePromotion
          key={`${article.title}${index}`}
          isBlue={isBlue}
          ctaText={ctaText}
          article={article}
        />
      ) : (
        <SwiperSlide key={`${article.title}${index}`}>
          {({ isActive }) => (
            <ArticlePromotion
              isBlue={isBlue}
              ctaText={ctaText}
              article={article}
              isActive={isActive}
            />
          )}
        </SwiperSlide>
      );
    });
  };

  return (
    <div
      className={clsx(styles["promotional-articles"], {
        [styles["blue"]]: isBlue,
      })}
      data-items={articles?.length || null}
    >
      <div className="wrapper">
        <div className={styles.container}>
          {isDesktop ? (
            buildArticles(articles)
          ) : (
            <SwiperCarousel
              className={clsx({ ["on-dark bullet-white"]: isBlue })}
              allowTouchMove={true}
              spaceBetween={10}
              slidesPerView={"auto"}
              centeredSlides={true}
              pagination={{
                clickable: true,
              }}
              modules={[A11y, Keyboard, Pagination]}
              keyboard={{ enabled: true, onlyInViewport: true }}
              a11y={{ enabled: true }}
            >
              {buildArticles(articles)}
            </SwiperCarousel>
          )}
        </div>
      </div>
    </div>
  );
};

const ArticlePromotion = (props: {
  article: ArticlePromotionType;
  ctaText?: string;
  isBlue?: boolean;
  isActive?: boolean;
}) => {
  const { article, ctaText, isBlue, isActive = true } = props;

  const href = `/${article.slug}`;

  return (
    <article
      aria-hidden={!isActive}
      // @ts-expect-error inert is not recognized by TS
      inert={!isActive ? "true" : undefined}
    >
      {!!article.image && "url" in article.image.fields && (
        <Link href={href}>
          <NextContentfulImage
            src={article.image.fields.url}
            alt={article.image.fields.altText}
            height={article.image.fields.height}
            width={article.image.fields.width}
            sizes="(max-width: 768px) 100vw, 30vw"
          />
          {article?.isVideo && (
            <div className={styles["play-icon"]}>
              <NextContentfulImage
                src={videoIcon}
                alt=""
                aria-hidden="true"
                aria-describedby="Contains a video"
                width={160}
                height={160}
              />
            </div>
          )}
        </Link>
      )}
      <div className={styles.text}>
        <div className={styles.line}></div>
        {article.subtitle && <p className={styles.label}>{article.subtitle}</p>}
        <h4>
          <Link
            href={href}
            {...(article?.isVideo
              ? { "aria-label": `${article?.title} contains a video` }
              : {})}
          >
            {article.title}
          </Link>
        </h4>
        <p>{article.description}</p>
        {article.authors && article.authors.length > 0 && (
          <p>
            By <strong>{commaConjunction.format(article.authors)}</strong>
          </p>
        )}
        {ctaText && (
          <Cta
            linkText={ctaText}
            type="tertiary"
            url={href}
            light={isBlue}
            icon="icon-arrow"
            ariaLabel={`${ctaText} ${article?.title}`}
          />
        )}
      </div>
    </article>
  );
};

export default PromotionalArticles;
